import { useState } from "react";
import { motion } from "framer-motion";
import { Minus, Plus, Scale } from "lucide-react";

const RATES = [650, 950, 1600, 1900, 2250, 2600, 2950, 3250, 3600, 3950];
const EXTRA_PER_LB = 550;

export const calcRate = (lbs) => {
  const w = Math.max(1, Math.ceil(lbs));
  return w <= 10 ? RATES[w - 1] : RATES[9] + (w - 10) * EXTRA_PER_LB;
};

const fmt = (n) => `JMD $${n.toLocaleString("en-JM")}`;

export const Rates = () => {
  const [lbs, setLbs] = useState(3);
  const price = calcRate(lbs);

  return (
    <section id="rates" data-testid="rates-section" className="py-28 sm:py-36 border-t border-white/10">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="mb-16"
        >
          <p className="font-mono-accent text-xs tracking-[0.3em] uppercase text-[#FF8A00] mb-4">
            Rates
          </p>
          <h2 className="font-display font-800 tracking-tighter text-4xl sm:text-5xl lg:text-6xl leading-[1.02]">
            Know your cost before you ship
          </h2>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-6">
          <motion.div
            data-testid="rate-calculator"
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6 }}
            className="rounded-2xl border border-white/10 bg-[#1C0D2C]/70 backdrop-blur-xl inner-glow p-8 sm:p-10 flex flex-col justify-between"
          >
            <div className="flex items-center gap-3 mb-10">
              <Scale className="w-5 h-5 text-[#FF8A00]" />
              <h3 className="font-display font-700 text-xl tracking-tight">
                Rate Calculator
              </h3>
            </div>

            <div>
              <div className="flex items-center justify-between mb-6">
                <span className="font-mono-accent text-xs tracking-widest uppercase text-[#A29DB0]">
                  Package weight
                </span>
                <div className="flex items-center gap-3">
                  <button
                    data-testid="weight-minus-btn"
                    onClick={() => setLbs((w) => Math.max(1, w - 1))}
                    className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center hover:bg-white/10 transition-colors duration-200"
                    aria-label="Decrease weight"
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                  <span
                    data-testid="weight-display"
                    className="font-mono-accent font-700 text-2xl w-24 text-center"
                  >
                    {lbs} lb{lbs > 1 ? "s" : ""}
                  </span>
                  <button
                    data-testid="weight-plus-btn"
                    onClick={() => setLbs((w) => Math.min(25, w + 1))}
                    className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center hover:bg-white/10 transition-colors duration-200"
                    aria-label="Increase weight"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <input
                data-testid="weight-slider"
                type="range"
                min="1"
                max="25"
                value={lbs}
                onChange={(e) => setLbs(Number(e.target.value))}
                className="w-full accent-[#FF8A00] h-1.5 cursor-pointer"
                aria-label="Package weight in pounds"
              />

              <div className="mt-10 border-t border-white/10 pt-8">
                <p className="font-mono-accent text-xs tracking-widest uppercase text-[#A29DB0] mb-2">
                  Estimated shipping cost
                </p>
                <p
                  data-testid="calculated-price"
                  className="font-mono-accent font-800 text-4xl sm:text-5xl text-[#FF8A00] tracking-tight"
                >
                  {fmt(price)}
                </p>
                {lbs > 10 && (
                  <p data-testid="extra-weight-note" className="mt-2 text-xs text-[#A29DB0]">
                    Includes {lbs - 10} extra lb{lbs - 10 > 1 ? "s" : ""} × JMD $550
                  </p>
                )}
              </div>
            </div>
          </motion.div>

          <motion.div
            data-testid="rates-table"
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6, delay: 0.12 }}
            className="rounded-2xl border border-white/10 bg-[#1C0D2C]/40 inner-glow p-8 sm:p-10"
          >
            <h3 className="font-display font-700 text-xl tracking-tight mb-6">
              Rate Card <span className="text-[#A29DB0] font-400 text-sm">/ air freight</span>
            </h3>
            <table className="w-full font-mono-accent text-sm">
              <tbody>
                {RATES.map((rate, i) => (
                  <tr
                    key={i}
                    data-testid={`rate-row-${i + 1}`}
                    className={`border-b border-white/5 transition-colors duration-200 hover:bg-white/5 ${
                      lbs === i + 1 ? "bg-[#FF8A00]/10" : ""
                    }`}
                  >
                    <td className="py-2.5 pr-4 text-[#A29DB0]">
                      {i + 1} lb{i + 1 > 1 ? "s" : ""}
                    </td>
                    <td className="py-2.5 text-right text-[#F8F7FA]">{fmt(rate)}</td>
                  </tr>
                ))}
                <tr className="transition-colors duration-200 hover:bg-white/5">
                  <td className="py-2.5 pr-4 text-[#A29DB0]">Each additional lb (11–25 lbs)</td>
                  <td className="py-2.5 text-right text-[#FF8A00]">+ JMD $550</td>
                </tr>
              </tbody>
            </table>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
